/* tmaAdmin — the root-only administration calls the mini app's Team screen
   uses (DESIGN40). Same endpoints the web Root panel calls; the Bearer token
   rides on every same-origin fetch through the global interceptor tmaApi
   seeds, so nothing here touches tokens.

     GET    /api/tma/links            → linked accounts + pending invites
     POST   /api/tma/invites          → { link, expires_at } for one user
     DELETE /api/tma/links/:userId    → unlink (+ void pending invites)
     GET/POST /api/auth/users…        → via ../auth/api (shared client) */

import { AuthApiError } from '../auth/api';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const TMA_BASE = API + '/api/tma';

export interface TmaLinkRow {
  user_id: string;
  telegram_username?: string | null;
  linked_at?: string | null;
}
export interface TmaInviteRow {
  user_id: string;
  expires_at?: string | null;
}
export interface TmaLinksResponse {
  links?: TmaLinkRow[];
  pending?: TmaInviteRow[];
  invites?: TmaInviteRow[];
}
export interface TmaInviteResponse {
  token: string;
  link: string;
  expires_at?: string | null;
}

export type MiniState =
  | { kind: 'linked'; telegramUsername: string | null; linkedAt: string | null }
  | { kind: 'invited'; expiresAt: string | null }
  | { kind: 'none' };

async function fail(res: Response): Promise<never> {
  const data = await res.json().catch(() => ({} as { error?: string; message?: string }));
  throw new AuthApiError(data.error || 'request_failed', data.message);
}

export async function getLinks(): Promise<TmaLinksResponse> {
  const res = await fetch(TMA_BASE + '/links');
  if (!res.ok) return fail(res);
  return res.json();
}

export async function issueInvite(userId: string): Promise<TmaInviteResponse> {
  const res = await fetch(TMA_BASE + '/invites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) return fail(res);
  return res.json();
}

export async function unlink(userId: string): Promise<void> {
  const res = await fetch(TMA_BASE + '/links/' + encodeURIComponent(userId), { method: 'DELETE' });
  if (!res.ok) return fail(res);
}

/** Fold /links into one state per user — derived, never authored here. */
export function miniStates(r: TmaLinksResponse): Map<string, MiniState> {
  const m = new Map<string, MiniState>();
  for (const p of r.pending || r.invites || []) {
    m.set(p.user_id, { kind: 'invited', expiresAt: p.expires_at ?? null });
  }
  for (const l of r.links || []) {
    m.set(l.user_id, {
      kind: 'linked',
      telegramUsername: l.telegram_username ?? null,
      linkedAt: l.linked_at ?? null,
    });
  }
  return m;
}

/* The rights the mini app cares about — the same bundles the web Root panel
   grants (architect:use opens the app; the sub-roles gate the tabs). */
export const PERM_USE = 'architect:use';
export const PERM_GENERATE = 'architect:generate';
export const PERM_ESTIMATE = 'architect:estimate';
export const PERM_PRICING = 'architect:pricing';
export const PERM_FEEDBACK = ['feedback:submit', 'feedback:view'];

export const TMA_ERROR_EN: Record<string, string> = {
  tma_not_configured: 'The mini app is not set up on the server yet — do that in the web Root panel.',
  user_not_found: 'That account no longer exists.',
  user_not_eligible: 'Grant Architect access first — only Architect accounts (and roots) can be invited.',
  user_exists: 'That username or email is already taken.',
  cannot_disable_self: 'You cannot disable your own account.',
  last_root: 'The last enabled root cannot be disabled.',
  request_failed: 'The request could not be completed.',
};

export function tmaErrorText(err: unknown): string {
  if (err instanceof AuthApiError) return TMA_ERROR_EN[err.code] || err.message || TMA_ERROR_EN.request_failed;
  return err instanceof Error ? err.message : TMA_ERROR_EN.request_failed;
}

/** A throwaway password for a Telegram-only teammate: the account exists so
    rights can be granted and the invite can bind it; nobody ever types it. */
export function randomPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const bytes = new Uint8Array(28);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/** The share deep-link: opens Telegram's native «send to…» with the invite. */
export function shareUrl(link: string, text: string): string {
  return 'https://t.me/share/url?url=' + encodeURIComponent(link) + '&text=' + encodeURIComponent(text);
}
